import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { queryKeys } from '@/lib/query'
import { USE_MOCKS } from '@/mocks/fixtures'

/* The live chat transport (0029).
 *
 * A WebSocket, unlike the bell beside it, and the difference is what each
 * carries. The bell is one-directional and SSE suits it. Chat needs the client
 * to say which thread it is looking at — `subscribe` — which a one-directional
 * stream cannot express without a second endpoint.
 *
 * THE SOCKET IS AN ACCELERATOR, NEVER THE SOURCE OF TRUTH, and everything here
 * is arranged to keep that true:
 *
 *  - events carry IDS, never message bodies. The server refuses to put text on
 *    the wire here, so a client that trusted the socket alone would render
 *    nothing; it has to fetch, and the fetch is scope-checked.
 *  - on every OPEN, every held thread is refetched. A frame dropped while the
 *    socket was down costs latency and nothing else.
 *  - the 60-second poll in useConversation stays. It covers a socket blocked
 *    by a corporate proxy, and the hub being process-local — a second replica
 *    does not know to wake this reader at all.
 *
 * One socket per tab, multiplexed across conversations: a page showing eight
 * threads must not open eight sockets, because browsers cap connections per
 * host and the server would hold eight goroutines for one reader.
 */

/** What the server sends. Ids and a verb — see the module comment. */
interface ChatSocketEvent {
  type: 'message' | 'edited' | 'deleted' | 'error'
  conversationId?: string
  messageId?: string
  code?: string
}

/** Where the socket lives, derived from the page's own origin.
 *
 *  Built from `location` rather than from VITE_API_BASE_URL because the socket
 *  must be same-origin: the cookie rides the handshake only if it is, and the
 *  server refuses any other Origin outright. A configurable host here would be
 *  a way to point the socket somewhere the cookie will not follow, which fails
 *  as an opaque handshake error rather than as a clear misconfiguration. */
function socketURL(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${window.location.host}/api/chat/stream`
}

/** First reconnect delay, and the ceiling it doubles towards.
 *
 *  A second is short enough that a dropped socket on a good connection is
 *  invisible; thirty is long enough that a server that is genuinely down is not
 *  hammered by every open tab in the office. */
const RECONNECT_MIN_MS = 1_000
const RECONNECT_MAX_MS = 30_000

export interface ChatSocket {
  /** Whether a socket is currently open. Drives nothing today; exposed so a
   *  connection indicator can be added without touching this file. */
  connected: boolean
  /** Watch a conversation. Idempotent, and refcounted — two panels on one
   *  thread unsubscribe independently without either cutting the other off. */
  subscribe: (conversationId: string) => void
  unsubscribe: (conversationId: string) => void
}

/** Opens the chat socket. Mount ONCE, in the app shell.
 *
 *  Mounted at the shell rather than in the panel for the reason
 *  useNotificationStream gives: a socket opened by a panel closes whenever the
 *  panel does, so events would stop the moment somebody navigated away from
 *  the thread they were watching. */
export function useChatSocket(enabled = true): ChatSocket {
  const queryClient = useQueryClient()
  const [connected, setConnected] = useState(false)

  /* The live socket, and what it is meant to be watching.
   *
   * Refs rather than state: none of this drives a render, and putting the
   * subscription set in state would re-run the connect effect on every
   * subscribe — reconnecting the socket each time somebody opened a thread. */
  const socketRef = useRef<WebSocket | null>(null)
  const wantedRef = useRef<Map<string, number>>(new Map())
  const backoffRef = useRef(RECONNECT_MIN_MS)

  /* Kept in a ref so the socket's own handlers can reach the current query
     client without being torn down and rebuilt when it changes.

     Synced in an effect rather than assigned during render: writing to a ref
     while rendering is what React forbids, and it is not needed here. Every
     reader of this ref is a socket handler, which fires on a message long after
     commit. The effect is declared BEFORE the connect effect below so that on
     any render where both run, the client is current before a socket exists to
     read it. */
  const clientRef = useRef(queryClient)
  useEffect(() => {
    clientRef.current = queryClient
  }, [queryClient])

  useEffect(() => {
    if (!enabled || USE_MOCKS || typeof WebSocket === 'undefined') return

    let closed = false
    let retry: number | undefined

    const open = () => {
      if (closed) return

      const socket = new WebSocket(socketURL())
      socketRef.current = socket

      socket.onopen = () => {
        if (closed) return
        setConnected(true)
        backoffRef.current = RECONNECT_MIN_MS

        /* Re-subscribe everything this tab was watching. The server holds no
           memory of a dropped connection, so a reconnect that did not re-ask
           would leave the panel silently unwatched. */
        for (const id of wantedRef.current.keys()) {
          socket.send(JSON.stringify({ type: 'subscribe', conversationId: id }))
        }

        /* THE line that keeps the socket an accelerator rather than a source
           of truth: refetch every thread, because anything that happened while
           the socket was down was never announced to us. Invalidating the
           whole chat subtree is cheaper to reason about than tracking which
           threads missed which events, and the queries are keyed per subject
           so only mounted ones actually refetch. */
        void clientRef.current.invalidateQueries({ queryKey: queryKeys.chat.all })
      }

      socket.onmessage = (raw) => {
        let event: ChatSocketEvent
        try {
          event = JSON.parse(raw.data as string) as ChatSocketEvent
        } catch {
          // A frame we cannot parse is a server we do not understand. Ignored
          // rather than fatal: the poll behind this still works.
          return
        }
        if (!event.conversationId) return

        /* Every event kind lands the same way: invalidate, and let the query
           refetch through the scoped endpoint. The socket never supplies
           content — see the module comment — so there is nothing to merge
           here, and an event for a thread nobody has open costs one no-op. */
        void clientRef.current.invalidateQueries({ queryKey: queryKeys.chat.all })
      }

      socket.onclose = () => {
        setConnected(false)
        socketRef.current = null
        if (closed) return

        /* Exponential backoff with a ceiling. The browser reconnects
           EventSource by itself; a WebSocket has to be re-opened by hand, and
           a fixed-interval retry from every open tab is how a struggling
           server is kept down. */
        retry = window.setTimeout(open, backoffRef.current)
        backoffRef.current = Math.min(backoffRef.current * 2, RECONNECT_MAX_MS)
      }

      socket.onerror = () => {
        // onclose always follows, and it owns the retry. Handling both would
        // schedule two reconnects for one failure.
        socket.close()
      }
    }

    open()

    return () => {
      closed = true
      if (retry) window.clearTimeout(retry)
      socketRef.current?.close()
      socketRef.current = null
    }
  }, [enabled])

  const send = (type: 'subscribe' | 'unsubscribe', conversationId: string) => {
    const socket = socketRef.current
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type, conversationId }))
    }
    // Not open yet: onopen replays everything in `wanted`, so a subscribe
    // issued during the handshake is not lost.
  }

  return {
    connected,
    subscribe: (conversationId: string) => {
      const held = wantedRef.current.get(conversationId) ?? 0
      wantedRef.current.set(conversationId, held + 1)
      // Only the FIRST watcher sends: a second panel on the same thread is
      // already covered, and re-subscribing would have the server join a room
      // it is in.
      if (held === 0) send('subscribe', conversationId)
    },
    unsubscribe: (conversationId: string) => {
      const held = wantedRef.current.get(conversationId) ?? 0
      if (held <= 1) {
        wantedRef.current.delete(conversationId)
        send('unsubscribe', conversationId)
        return
      }
      wantedRef.current.set(conversationId, held - 1)
    },
  }
}
